/**
 * DSAT Analytics Panel — student-facing dashboard.
 *
 * Backend: GET /api/exam/v2/me/dashboard  (with filters range_key / section / test_type / source)
 * All results shown here are Winhouse Practice Results — never labelled as
 * College Board scores.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, TrendingUp, Timer, Target, Loader2, Filter, Lightbulb, MessageSquare } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const RANGES = [
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
  { key: 'all', label: 'All time' },
];
const SECTIONS = [
  { key: '', label: 'All sections' },
  { key: 'rw', label: 'Reading & Writing' },
  { key: 'math', label: 'Math' },
];
const TYPES = [
  { key: '', label: 'All test types' },
  { key: 'full_mock', label: 'Full Mocks' },
  { key: 'full_section', label: 'Full Section' },
  { key: 'short_section', label: 'Short Section' },
  { key: 'topic', label: 'Topic Practice' },
];
const SOURCES = [
  { key: '', label: 'All sources' },
  { key: 'assigned', label: 'Tutor Assigned' },
  { key: 'independent', label: 'Independent Practice' },
];

export default function DSATAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ range_key: '90d', section: '', test_type: '', source: '' });

  const setF = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ exam_type: 'dsat' });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      const r = await ax.get(`/api/exam/v2/me/dashboard?${params.toString()}`);
      setData(r.data);
    } catch (_e) { setData({ error: 'Could not load analytics' }); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-5" data-testid="dsat-analytics-panel">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="h-5 w-5 text-red-700" />
        <div>
          <h2 className="text-base font-bold text-gray-900">Digital SAT Performance Dashboard</h2>
          <div className="text-[11px] text-gray-500">
            Winhouse Practice Results · not official College Board scores
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-3">
        <Filter className="h-3.5 w-3.5 text-gray-400" />
        <FilterPill options={RANGES} value={filters.range_key} onChange={(v) => setF({ range_key: v })} testid="an-range" />
        <FilterPill options={SECTIONS} value={filters.section} onChange={(v) => setF({ section: v })} testid="an-section" />
        <FilterPill options={TYPES} value={filters.test_type} onChange={(v) => setF({ test_type: v })} testid="an-type" />
        <FilterPill options={SOURCES} value={filters.source} onChange={(v) => setF({ source: v })} testid="an-source" />
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-6 flex items-center gap-2" data-testid="an-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : data?.empty || data?.total_attempts === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center" data-testid="an-empty">
          Complete a test to unlock your dashboard.
        </div>
      ) : (
        <>
          {/* Top KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Kpi label="Total attempts" value={data.total_attempts} />
            <Kpi label="Assignments done" value={data.assignments_completed} />
            <Kpi label="Winhouse average" value={data.average_pct != null ? `${data.average_pct}%` : '—'} />
            <Kpi label="Estimated band" value={data.estimated_band} accent />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <TrendCard label="Reading & Writing trend" data={data.rw_trend} colour="rw" />
            <TrendCard label="Math trend" data={data.math_trend} colour="math" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <StatCard icon={<Target className="h-4 w-4" />} label="Best result">
              <div className="text-2xl font-bold text-emerald-700">
                {data.best_pct != null ? `${data.best_pct}%` : '—'}
              </div>
            </StatCard>
            <StatCard icon={<Timer className="h-4 w-4" />} label="Avg time per question">
              <div className="text-2xl font-bold text-gray-900">
                {data.avg_time_seconds_per_question != null ? `${data.avg_time_seconds_per_question}s` : '—'}
              </div>
              <div className="text-[11px] text-gray-500 mt-1">Timed vs untimed: {data.timed_avg_pct ?? '—'}% / {data.untimed_avg_pct ?? '—'}%</div>
            </StatCard>
            <StatCard icon={<TrendingUp className="h-4 w-4" />} label="Unanswered rate">
              <div className="text-2xl font-bold text-gray-900">{data.unanswered_rate_pct ?? 0}%</div>
              <div className="text-[11px] text-gray-500 mt-1">
                Flagged accuracy: {data.flagged_performance_pct != null ? `${data.flagged_performance_pct}% (${data.flagged_total} flagged)` : '—'}
              </div>
            </StatCard>
          </div>

          {data.speed_summary && (
            <Card className="p-3 mb-4 bg-gray-50 border-dashed">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">Accuracy vs speed</div>
              <div className="text-[12px] text-gray-700 grid grid-cols-2 gap-2">
                <div>Fast questions (avg {data.speed_summary.fast_avg_seconds}s): <b>{data.speed_summary.fast_accuracy_pct ?? '—'}%</b></div>
                <div>Slow questions (avg {data.speed_summary.slow_avg_seconds}s): <b>{data.speed_summary.slow_accuracy_pct ?? '—'}%</b></div>
              </div>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <MasteryBlock title="Domain mastery" items={data.domain_mastery} testid="an-domain" />
            <MasteryBlock title="Skill mastery" items={data.skill_mastery} testid="an-skill" />
            <MasteryBlock title="Difficulty performance" items={data.difficulty_performance} testid="an-difficulty" />
          </div>

          {data.full_mock_trend?.length > 0 && (
            <Card className="p-3 mb-4">
              <div className="text-xs font-bold text-gray-800 mb-1">Full mock history</div>
              <TrendBars values={data.full_mock_trend} colour="fm" />
            </Card>
          )}

          {(data.weak_skills_recommendation?.length > 0) && (
            <Card className="p-3 mb-4 bg-amber-50 border-amber-200" data-testid="an-recommendation">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="h-4 w-4 text-amber-700" />
                <div className="text-xs font-bold text-gray-900">Recommended next practice</div>
              </div>
              <div className="text-[12px] text-gray-700">
                Focus your next session on: {data.weak_skills_recommendation.map((s) => <Badge key={s} className="bg-white text-amber-900 border border-amber-300 mx-1">{s}</Badge>)}
              </div>
            </Card>
          )}

          {data.recent_tutor_feedback?.length > 0 && (
            <Card className="p-3">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="h-4 w-4 text-blue-700" />
                <div className="text-xs font-bold text-gray-900">Recent tutor feedback</div>
              </div>
              <div className="space-y-2" data-testid="an-tutor-feedback">
                {data.recent_tutor_feedback.map((r) => (
                  <div key={r.id} className="text-[12px] text-gray-800 border-l-2 border-blue-500 pl-2">
                    <div className="text-[10px] text-gray-500">{r.tutor_name || r.tutor_email} · {new Date(r.created_at).toLocaleString()}</div>
                    <div>{r.comment || '—'}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </Card>
  );
}

function FilterPill({ options, value, onChange, testid }) {
  return (
    <select className="border rounded px-2 py-1 text-xs h-8 bg-white" value={value}
      onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`border rounded-xl p-3 ${accent ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
      <div className={`text-xl font-bold mt-0.5 ${accent ? 'text-red-800' : 'text-gray-900'}`}>{value ?? '—'}</div>
    </div>
  );
}

function StatCard({ icon, label, children }) {
  return (
    <div className="border rounded-xl p-3 bg-white">
      <div className="flex items-center gap-1 text-gray-500">{icon}
        <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span></div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function TrendCard({ label, data, colour }) {
  return (
    <div className="border rounded-xl p-3 bg-white">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-1">{label}</div>
      {data?.length ? (
        <TrendBars values={data} colour={colour} />
      ) : (
        <div className="text-[11px] text-gray-500 italic">No data in this filter yet.</div>
      )}
    </div>
  );
}

function TrendBars({ values, colour = 'rw' }) {
  const max = Math.max(100, ...values);
  const c = colour === 'math' ? 'bg-blue-500'
    : colour === 'fm' ? 'bg-red-500'
    : 'bg-emerald-500';
  return (
    <div className="flex items-end gap-1 h-24" data-testid={`trend-${colour}`}>
      {values.map((v, i) => (
        <div key={i} className="flex-1 flex flex-col items-center">
          <div className={`${c} rounded-t w-full`} style={{ height: `${(v / max) * 100}%` }} title={`${v}%`} />
          <div className="text-[9px] text-gray-400 mt-1">{v}</div>
        </div>
      ))}
    </div>
  );
}

function MasteryBlock({ title, items, testid }) {
  const top = (items || []).slice(0, 8);
  return (
    <div className="border rounded-xl p-3 bg-white" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">{title}</div>
      {top.length === 0 && <div className="text-[11px] text-gray-500 italic">No data yet.</div>}
      {top.map((r) => (
        <div key={r.name} className="mb-1.5">
          <div className="flex items-center justify-between text-[11px] text-gray-800">
            <span className="capitalize truncate mr-2">{r.name}</span>
            <span className="font-bold">{r.mastery_pct}%</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded overflow-hidden">
            <div className={`h-full ${r.mastery_pct >= 75 ? 'bg-emerald-500' : r.mastery_pct >= 50 ? 'bg-amber-500' : 'bg-rose-500'}`}
              style={{ width: `${Math.max(3, Math.min(100, r.mastery_pct))}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}
